import { EntityType } from "../../Common.types.ts";

export interface SearchResultViewModel {
    entityId: string;
    entityType: EntityType;
    entityName: string;
    parentId?: string;
    subjectEntityType?: EntityType;
    subjectEntityId?: string;
}

export enum DocumentEntityType {
    Contract = 1,
    Task = 2,
    LegalEntity = 3,
    Policy = 4,
    ProcessingActivity = 5,
    System = 6,
    IncidentLog = 7,
    AnnualWheelActivity = 8,
    ConversationAttachment = 9,
    RiskAssessment = 10,
    Process = 11,
}

export interface ContentSnippetWithPage {
    text: string;
    pageNumber?: number;
}

export interface DocumentSearchResult {
    id: string;
    documentId: string;
    documentVersionId: string;
    documentTitle: string;
    documentDate: Date;
    fileName: string | undefined;
    versionNumber?: number;
    entityType: DocumentEntityType;
    entityId: string;
    entityTitle: string;
    contentSnippets: Array<ContentSnippetWithPage>;
    parentEntityType?: DocumentEntityType;
    parentEntityId?: string;
    parentEntityTitle?: string;
    hasMoreOccurrences: boolean;
    content: string;
}

export interface DocumentSearchResponse {
    results: Array<DocumentSearchResult>;
    totalCount: number;
    hasMoreResults: boolean;
}

export type SearchFilterType = "system" | "documents";

export function mapDocumentEntityTypeToEntityType(documentEntityType: DocumentEntityType): EntityType {
    // Below legal entity and policy types map to LegalEntityDocument and PolicyDocument. This is because
    // the permissions checks for legal entities and legal entity documents differ (the same for policies and policy documents).
    // For other types, the checks are the same for the main entity and the underlying documents.
    switch (documentEntityType) {
        case DocumentEntityType.Contract:
            return EntityType.Contract;
        case DocumentEntityType.Task:
            return EntityType.Task;
        case DocumentEntityType.LegalEntity:
            return EntityType.LegalEntityDocument;
        case DocumentEntityType.Policy:
            return EntityType.PolicyDocument;
        case DocumentEntityType.ProcessingActivity:
            return EntityType.ProcessingActivity;
        case DocumentEntityType.System:
            return EntityType.System;
        case DocumentEntityType.IncidentLog:
            return EntityType.IncidentLog;
        case DocumentEntityType.Process:
            return EntityType.Process;
        case DocumentEntityType.AnnualWheelActivity:
            return EntityType.AnnualWheelActivity;
        case DocumentEntityType.RiskAssessment:
            return EntityType.Risk;
        case DocumentEntityType.ConversationAttachment:
            // AI chat attachments live outside the Document table; callers must route through the attachment download URL instead.
            throw new Error("ConversationAttachment has no corresponding EntityType");
    }
}

export function mapDocumentEntityTypeToEntityTypeForTranslation(documentEntityType: DocumentEntityType): EntityType {
    const entityType = mapDocumentEntityTypeToEntityType(documentEntityType);
    if (documentEntityType === DocumentEntityType.LegalEntity) {
        return EntityType.LegalEntity;
    }
    return entityType;
}
